/**
=========================================================
* PCS Admin React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import React, { useState, useEffect } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import MDButton from "components/MDButton";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";

// PCS Admin React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";

// PCS Admin React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// PCS Admin React services
import { getCharges, getConcepts, getProjects, getClients, updateCharge } from "../../services/firestore";

function Dashboard() {
  const [stats, setStats] = useState({
    totalProjects: 0,
    activeProjects: 0,
    totalClients: 0,
    totalConcepts: 0,
  });

  const [pendingCharges, setPendingCharges] = useState([]);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    charge: null
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedCards, setExpandedCards] = useState({});
  const itemsPerPage = 9; // Aumentado a 9 cards por página

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Obtener todos los datos necesarios
        const [chargesData, conceptsData, projectsData, clientsData] = await Promise.all([
          getCharges(),
          getConcepts(),
          getProjects(),
          getClients()
        ]);

        // Calcular estadísticas
        const totalProjects = projectsData.length;
        const activeProjects = projectsData.filter(p => p.status === "active").length;
        const totalClients = clientsData.length;
        const totalConcepts = conceptsData.length;

        setStats({
          totalProjects,
          activeProjects,
          totalClients,
          totalConcepts,
        });

        // Procesar cargos pendientes
        const pendingChargesWithDetails = chargesData
          .filter(charge => charge.estado === "Pendiente")
          .map(charge => {
            const concept = conceptsData.find(c => c.id === charge.concepto_id);
            const project = projectsData.find(p => p.id === charge.proyecto_id);
            const client = clientsData.find(c => c.id === project?.client_id);

            // Calcular total pagado del proyecto
            const projectCharges = chargesData.filter(c => 
              c.proyecto_id === charge.proyecto_id && 
              c.estado === "Pagado"
            );
            const totalPagado = projectCharges.reduce((sum, c) => sum + (c.monto || 0), 0);

            return {
              id: charge.id,
              ...charge,
              concepto_nombre: concept?.nombre || "Sin concepto",
              proyecto_nombre: project?.nombre || "Sin proyecto",
              cliente_nombre: project?.cliente || "Sin cliente",
              fecha_vencimiento: charge.fecha_vencimiento,
              monto: charge.monto,
              total_pagado: totalPagado,
              dias_vencidos: Math.floor((new Date() - new Date(charge.fecha_vencimiento)) / (1000 * 60 * 60 * 24))
            };
          })
          .sort((a, b) => new Date(a.fecha_vencimiento) - new Date(b.fecha_vencimiento));

        // Agrupar cargos por días vencidos
        const cargosAgrupados = pendingChargesWithDetails.reduce((acc, charge) => {
          let grupo;
          const fechaVencimiento = new Date(charge.fecha_vencimiento).toLocaleDateString();
          
          if (charge.dias_vencidos < 0) {
            grupo = `Próximos - ${fechaVencimiento}`;
          } else if (charge.dias_vencidos === 0) {
            grupo = "Vence Hoy";
          } else if (charge.dias_vencidos <= 7) {
            grupo = `Vencidos (1-7 días) - ${fechaVencimiento}`;
          } else if (charge.dias_vencidos <= 30) {
            grupo = `Vencidos (8-30 días) - ${fechaVencimiento}`;
          } else {
            grupo = `Vencidos (+30 días) - ${fechaVencimiento}`;
          }

          if (!acc[grupo]) {
            acc[grupo] = [];
          }
          acc[grupo].push(charge);
          return acc;
        }, {});

        // Ordenar los grupos por fecha
        const gruposOrdenados = Object.entries(cargosAgrupados).sort(([grupoA], [grupoB]) => {
          const fechaA = grupoA.split(" - ")[1];
          const fechaB = grupoB.split(" - ")[1];
          if (!fechaA || !fechaB) return 0;
          return new Date(fechaA) - new Date(fechaB);
        });

        setPendingCharges(Object.fromEntries(gruposOrdenados));
      } catch (error) {
        console.error("Error al obtener datos:", error);
      }
    };

    fetchData();
  }, []);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: "MXN",
    }).format(amount);
  };

  const handleMarkAsPaid = async (charge) => {
    try {
      await updateCharge(charge.id, { estado: "Pagado" });
      // Actualizar el estado local
      setPendingCharges(prev => {
        const newCharges = { ...prev };
        Object.keys(newCharges).forEach(key => {
          newCharges[key] = newCharges[key].filter(c => c.id !== charge.id);
        });
        // Eliminar grupos vacíos
        Object.keys(newCharges).forEach(key => {
          if (newCharges[key].length === 0) {
            delete newCharges[key];
          }
        });
        return newCharges;
      });
      setConfirmDialog({ open: false, charge: null });
    } catch (error) {
      console.error("Error al marcar como pagado:", error);
    }
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const toggleCardExpansion = (cardId) => {
    setExpandedCards(prev => ({
      ...prev,
      [cardId]: !prev[cardId]
    }));
  };

  // Calcular el total de páginas
  const totalItems = Object.values(pendingCharges).reduce((acc, curr) => acc + curr.length, 0);
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="dark"
                icon="folder"
                title="Total Proyectos"
                count={stats.totalProjects}
                percentage={{
                  color: "success",
                  amount: `${stats.activeProjects} activos`,
                  label: "proyectos activos",
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                icon="people"
                title="Total Clientes"
                count={stats.totalClients}
                percentage={{
                  color: "success",
                  amount: "",
                  label: "clientes registrados",
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="success"
                icon="list"
                title="Total Conceptos"
                count={stats.totalConcepts}
                percentage={{
                  color: "success",
                  amount: "",
                  label: "conceptos registrados",
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="primary"
                icon="event"
                title="Cobros Pendientes"
                count={Object.keys(pendingCharges).length}
                percentage={{
                  color: "warning",
                  amount: "",
                  label: "por cobrar",
                }}
              />
            </MDBox>
          </Grid>
        </Grid>

        <MDBox mt={4.5}>
          <MDTypography variant="h5" fontWeight="medium" mb={3}>
            Cobros Pendientes
          </MDTypography>
          {Object.entries(pendingCharges).map(([grupo, cargos]) => {
            const [tipo, fecha] = grupo.split(" - ");
            const totalGrupo = cargos.reduce((sum, c) => sum + c.monto, 0);
            
            return (
              <MDBox key={grupo} mb={4}>
                <MDBox 
                  display="flex" 
                  justifyContent="start" 
                  alignItems="center" 
                  mb={2}
                >
                  <MDTypography variant="h6" color="info">
                    {tipo} {fecha && `- ${fecha}`} ({cargos.length})
                  </MDTypography>
                  <MDTypography 
                    variant="h6" 
                    color="warning" 
                    fontWeight="bold"
                    sx={{
                      backgroundColor: "rgba(255, 152, 0, 0.1)",
                      padding: "4px 12px",
                      borderRadius: "8px"
                    }}
                  >
                    Total: {formatCurrency(totalGrupo)}
                  </MDTypography>
                </MDBox>
                <Grid container spacing={3}>
                  {cargos
                    .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                    .map((charge) => (
                    <Grid item xs={12} md={6} lg={4} key={charge.id}>
                      <Card 
                        sx={{
                          height: expandedCards[charge.id] ? "auto" : "180px",
                          display: "flex",
                          flexDirection: "column",
                          transition: "all 0.3s ease",
                          background: "linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)",
                          borderRadius: "16px",
                          overflow: "hidden",
                          cursor: "pointer",
                          "&:hover": {
                            transform: "translateY(-8px)",
                            boxShadow: "0 12px 24px rgba(0,0,0,0.1)",
                          },
                        }}
                        onClick={() => toggleCardExpansion(charge.id)}
                      >
                        <MDBox 
                          p={3}
                          sx={{
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            position: "relative",
                            "&::before": {
                              content: '""',
                              position: "absolute",
                              top: 0,
                              left: 0,
                              right: 0,
                              height: "4px",
                              background: charge.dias_vencidos > 0 
                                ? "linear-gradient(90deg, #f44336 0%, #ff9800 100%)"
                                : "linear-gradient(90deg, #2196f3 0%, #4caf50 100%)",
                            }
                          }}
                        >
                          <MDBox 
                            display="flex" 
                            justifyContent="space-between" 
                            alignItems="center" 
                            mb={2}
                            sx={{
                              borderBottom: "2px solid rgba(0,0,0,0.05)",
                              pb: 2
                            }}
                          >
                            <MDTypography 
                              variant="h5" 
                              fontWeight="bold" 
                              color="info"
                              sx={{
                                fontSize: "1.1rem",
                                maxWidth: "60%",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap"
                              }}
                            >
                              {charge.concepto_nombre}
                            </MDTypography>
                            <MDTypography 
                              variant="h5" 
                              color="warning" 
                              fontWeight="bold"
                              sx={{
                                backgroundColor: "rgba(255, 152, 0, 0.1)",
                                padding: "6px 16px",
                                borderRadius: "12px",
                                fontSize: "1.1rem",
                                boxShadow: "0 2px 8px rgba(255, 152, 0, 0.1)"
                              }}
                            >
                              {formatCurrency(charge.monto)}
                            </MDTypography>
                          </MDBox>

                          <MDBox 
                              mb={2}
                              sx={{
                                backgroundColor: "rgba(33, 150, 243, 0.05)",
                                padding: "12px",
                                borderRadius: "8px"
                              }}
                            >
                              <MDTypography variant="button" color="text" fontWeight="bold">
                                Proyecto:
                              </MDTypography>
                              <MDTypography 
                                variant="button" 
                                color="text" 
                                ml={1}
                                sx={{
                                  fontWeight: "medium",
                                  fontSize: "0.9rem"
                                }}
                              >
                                {charge.proyecto_nombre}
                              </MDTypography>
                            </MDBox>

                          {/* Campos expandibles */}
                          <MDBox
                            sx={{
                              maxHeight: expandedCards[charge.id] ? "500px" : "0",
                              overflow: "hidden",
                              transition: "all 0.3s ease-in-out",
                              opacity: expandedCards[charge.id] ? 1 : 0,
                              transform: expandedCards[charge.id] ? "translateY(0)" : "translateY(-10px)",
                            }}
                          >
                            

                            <MDBox 
                              mb={2}
                              sx={{
                                backgroundColor: "rgba(33, 150, 243, 0.05)",
                                padding: "12px",
                                borderRadius: "8px"
                              }}
                            >
                              <MDTypography variant="button" color="text" fontWeight="bold">
                                Cliente:
                              </MDTypography>
                              <MDTypography 
                                variant="button" 
                                color="text" 
                                ml={1}
                                sx={{
                                  fontWeight: "medium",
                                  fontSize: "0.9rem"
                                }}
                              >
                                {charge.cliente_nombre}
                              </MDTypography>
                            </MDBox>

                            <MDBox 
                              mb={2}
                              sx={{
                                backgroundColor: "rgba(76, 175, 80, 0.05)",
                                padding: "12px",
                                borderRadius: "8px"
                              }}
                            >
                              <MDTypography variant="button" color="text" fontWeight="bold">
                                Total Pagado:
                              </MDTypography>
                              <MDTypography 
                                variant="button" 
                                color="success" 
                                ml={1}
                                sx={{
                                  backgroundColor: "rgba(76, 175, 80, 0.1)",
                                  padding: "4px 12px",
                                  borderRadius: "8px",
                                  fontWeight: "bold",
                                  fontSize: "0.9rem"
                                }}
                              >
                                {formatCurrency(charge.total_pagado)}
                              </MDTypography>
                            </MDBox>
                          </MDBox>

                          <MDBox 
                            mt="auto" 
                            display="flex" 
                            gap={2}
                            onClick={(e) => e.stopPropagation()}
                            sx={{
                              opacity: expandedCards[charge.id] ? 1 : 0,
                              transition: "opacity 0.3s ease",
                              pointerEvents: expandedCards[charge.id] ? "auto" : "none"
                            }}
                          >
                            <MDButton
                              variant="gradient"
                              color="success"
                              fullWidth
                              onClick={() => setConfirmDialog({ open: true, charge })}
                              sx={{
                                borderRadius: "12px",
                                textTransform: "none",
                                fontSize: "0.9rem",
                                fontWeight: "bold",
                                padding: "10px",
                                background: "linear-gradient(90deg, #4caf50 0%, #388e3c 100%)",
                                boxShadow: "0 4px 12px rgba(76, 175, 80, 0.2)",
                                "&:hover": {
                                  background: "linear-gradient(90deg, #388e3c 0%, #2e7d32 100%)",
                                  boxShadow: "0 6px 16px rgba(76, 175, 80, 0.3)",
                                }
                              }}
                            >
                              Marcar como Pagado
                            </MDButton>
                            <MDButton
                              variant="gradient"
                              color="info"
                              fullWidth
                              href={`/projects/${charge.proyecto_id}`}
                              sx={{
                                borderRadius: "12px",
                                textTransform: "none",
                                fontSize: "0.9rem",
                                fontWeight: "bold",
                                padding: "10px",
                                background: "linear-gradient(90deg, #2196f3 0%, #1976d2 100%)",
                                boxShadow: "0 4px 12px rgba(33, 150, 243, 0.2)",
                                "&:hover": {
                                  background: "linear-gradient(90deg, #1976d2 0%, #1565c0 100%)",
                                  boxShadow: "0 6px 16px rgba(33, 150, 243, 0.3)",
                                }
                              }}
                            >
                              Ver Detalles
                            </MDButton>
                          </MDBox>
                        </MDBox>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </MDBox>
            );
          })}

          {totalPages > 1 && (
            <MDBox mt={4} display="flex" justifyContent="center">
              <Stack spacing={2}>
                <Pagination 
                  count={totalPages} 
                  page={currentPage} 
                  onChange={handlePageChange}
                  color="primary"
                  size="large"
                  sx={{
                    '& .MuiPaginationItem-root': {
                      fontSize: '1rem',
                      fontWeight: 'bold',
                    },
                  }}
                />
              </Stack>
            </MDBox>
          )}
        </MDBox>
      </MDBox>

      {/* Diálogo de confirmación */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, charge: null })}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            padding: "16px",
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: "bold" }}>
          Confirmar Pago
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Estás seguro de que deseas marcar como pagado el siguiente cargo?
          </DialogContentText>
          {confirmDialog.charge && (
            <MDBox mt={2}>
              <MDTypography variant="subtitle1" fontWeight="bold">
                Concepto: {confirmDialog.charge.concepto_nombre}
              </MDTypography>
              <MDTypography variant="subtitle1" fontWeight="bold">
                Monto: {formatCurrency(confirmDialog.charge.monto)}
              </MDTypography>
              <MDTypography variant="subtitle1" fontWeight="bold">
                Proyecto: {confirmDialog.charge.proyecto_nombre}
              </MDTypography>
            </MDBox>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: "16px" }}>
          <MDButton
            onClick={() => setConfirmDialog({ open: false, charge: null })}
            color="error"
            variant="outlined"
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: "bold",
            }}
          >
            Cancelar
          </MDButton>
          <MDButton
            onClick={() => handleMarkAsPaid(confirmDialog.charge)}
            color="success"
            variant="contained"
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: "bold",
              background: "linear-gradient(90deg, #4caf50 0%, #388e3c 100%)",
            }}
          >
            Confirmar Pago
          </MDButton>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default Dashboard;
